import React from 'react';
import { Avatar, Box, Chip, lighten, Typography, useMediaQuery, useTheme } from '@mui/material';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { InformationChip } from '@europrocurement/flexy-components';
import { Link } from 'react-router-dom';

const themeColor = ['primary', 'secondary', 'warning', 'success', 'error'] as const;
type ThemeColor = (typeof themeColor)[number];
type HexColor = string;

export type DetailOrderTitleProps = {
    title: string;
    /** Statut global de la commande */
    orderStatus?: string;
    /** Informations du prescripteur */
    prescripteur?: {
        id: number;
        nom: string;
        codeComptable: string;
    };
    /** Informations de l'offre */
    offre?: {
        id: number;
        libelle: string;
    };
    icon?: IconName;
    color?: ThemeColor | HexColor;
    prescripteurDetailUrl?: string;
};

const DetailOrderTitle: React.FunctionComponent<DetailOrderTitleProps> = function ({
    title,
    orderStatus,
    prescripteur,
    offre,
    icon = 'shopping-cart',
    color = 'primary',
    prescripteurDetailUrl,
}) {
    const isThemeColor = (str: string): str is ThemeColor => themeColor.includes(str as ThemeColor);
    const { palette, breakpoints } = useTheme();
    const downMdBreakpoint = useMediaQuery(breakpoints.down('md'));
    const baseColor = isThemeColor(color) ? palette[color].main : color;

    const avatarStyle = {
        display: 'flex',
        borderRadius: '8px',
        backgroundColor: lighten(baseColor, 0.92),
        minHeight: '75px',
        minWidth: '75px',
    };

    const titleWrapperStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '0 8px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginBottom: '8px',
    };

    const titleStyle = {
        maxWidth: '70%',
        overflow: 'hidden',
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
    };

    if (downMdBreakpoint) {
        avatarStyle.display = 'none';
        titleWrapperStyle.flexDirection = 'column';
        titleWrapperStyle.gap = '8px 0';
        titleWrapperStyle.alignItems = 'flex-start';
        titleWrapperStyle.marginBottom = '0.5em';
        titleStyle.maxWidth = '100%';
    }

    return (
        <Box sx={{ display: 'flex', gap: '0 24px', marginBottom: '16px' }}>
            <Avatar sx={avatarStyle}>
                <FaOptionIcon
                    icon={['fasl', icon]}
                    color={color}
                />
            </Avatar>
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                {/* Titre principal avec statut */}
                <Box sx={titleWrapperStyle}>
                    <Typography
                        variant="l2dh5"
                        sx={titleStyle}
                    >
                        {title}
                    </Typography>
                    {orderStatus && (
                        <Chip
                            label={orderStatus}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ marginLeft: '8px' }}
                        />
                    )}
                </Box>

                {/* Informations complémentaires : prescripteur et nombre de dossiers */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    {prescripteur && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography
                                variant="l2dbodys"
                                color="text.gris60"
                            >
                                Prescripteur:
                            </Typography>
                            <Typography
                                variant="l2dbodys"
                                color="text.gris70"
                                sx={{ fontWeight: 500 }}
                            >
                                {prescripteur.nom}
                            </Typography>
                            <Typography
                                variant="l2dbodys"
                                color="text.gris50"
                            >
                                ({prescripteur.codeComptable})
                            </Typography>
                            {prescripteurDetailUrl ? (
                                <Link
                                    to={prescripteurDetailUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none' }}
                                >
                                    <InformationChip
                                        color={palette.grey[600]}
                                        label={<Typography variant="l2dbodys">Fiche</Typography>}
                                        icon={['fasl', 'arrow-up-right-from-square']}
                                    />
                                </Link>
                            ) : null}
                            <FaOptionIcon
                                icon={['fasl', 'chevron-right']}
                                sx={{
                                    fontSize: '10px',
                                }}
                                size="sm"
                            />
                            <Typography
                                variant="l2dbodys"
                                color="text.gris60"
                            >
                                Offre:
                            </Typography>
                            <InformationChip
                                label={<Typography variant="l2dbodys">{offre?.libelle}</Typography>}
                                icon={['fasl', 'star-circle']}
                                color={palette.grey[600]}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DetailOrderTitle;
